import { useEffect, useState } from 'react';
import { VerdictStamp } from './VerdictStamp';

interface Props {
  /** 0-100. */
  score: number;
  rating: string;
  found: number;
  total: number;
  missed: number;
  falseAlarms: number;
}

/**
 * The verdict card.
 *
 * Shared by both game modes so the moment of scoring looks the same wherever
 * it happens. The number counts up and the stamp thumps down, because the
 * result is the beat the whole level has been building to and a static figure
 * throws it away.
 */
export function ScorePanel({ score, rating, found, total, missed, falseAlarms }: Props) {
  const shown = useCountUp(score);

  return (
    <div className="score">
      <VerdictStamp score={score} />

      {/* The animated figure is decorative; assistive tech gets the final value
          once, rather than a stream of intermediate numbers. */}
      <div className="score__value" aria-hidden="true">
        {shown}
      </div>
      <span className="sr-only">Score {score} out of 100.</span>

      <div className="score__rating">{rating}</div>

      <div className="meter" aria-hidden="true">
        <span className="meter__fill" style={{ width: `${score}%` }} />
      </div>

      <div className="score__breakdown">
        <span>
          <span className="stat__label">Found</span>
          <span className="stat__value">
            {found} / {total}
          </span>
        </span>
        <span>
          <span className="stat__label">Missed</span>
          <span className="stat__value">{missed}</span>
        </span>
        <span>
          <span className="stat__label">False alarms</span>
          <span className="stat__value">{falseAlarms}</span>
        </span>
      </div>
    </div>
  );
}

/** How long the count-up takes, start to finish. */
const COUNT_MS = 620;

/**
 * Count from zero to `target` over a fixed duration, easing out.
 *
 * Fixed duration rather than fixed step: a score of 8 and a score of 96 have to
 * finish at the same time as the meter beside them, or the two animations pull
 * apart. Honours prefers-reduced-motion by jumping straight to the value --
 * the global CSS rule cannot reach a number being incremented in JavaScript.
 */
function useCountUp(target: number): number {
  const [value, setValue] = useState(target);

  useEffect(() => {
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      setValue(target);
      return;
    }

    let frame = 0;
    const started = performance.now();

    const tick = (now: number) => {
      const progress = Math.min(1, (now - started) / COUNT_MS);
      // easeOutCubic: fast off the mark, settling onto the final number.
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    setValue(0);
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return value;
}
